import React, { useState } from 'react';
import { Mail, Send, AlertTriangle } from 'lucide-react';

/**
 * Email Send Modal
 * Modal for sending invoice or reminder emails
 */
const EmailSendModal = ({ invoice, language, emailStatus, onClose, onSendInvoice, onSendReminder, sending }) => {
  const [sendType, setSendType] = useState('invoice'); // invoice or reminder
  const [reminderType, setReminderType] = useState('first');
  const [includePdf, setIncludePdf] = useState(true);

  const t = language === 'fr' ? {
    title: 'Envoyer par email',
    invoice: 'Facture',
    to: 'Destinataire',
    type: 'Type d\'envoi',
    sendInvoice: 'Envoyer la facture',
    sendReminder: 'Envoyer un rappel',
    reminderType: 'Type de rappel',
    firstReminder: '1er rappel',
    secondReminder: '2ème rappel',
    formalNotice: 'Mise en demeure',
    includePdf: 'Joindre le PDF de la facture',
    send: 'Envoyer',
    cancel: 'Annuler',
    sending: 'Envoi en cours...',
    warning: 'Attention',
    formalWarning: 'La mise en demeure est un document juridique. Assurez-vous que la dette est confirmée.',
    notConfigured: 'Le service email n\'est pas configuré. Ajoutez SENDGRID_API_KEY et SENDER_EMAIL dans le fichier backend/.env',
  } : {
    title: 'Verzenden per e-mail',
    invoice: 'Factuur',
    to: 'Ontvanger',
    type: 'Verzendtype',
    sendInvoice: 'Factuur verzenden',
    sendReminder: 'Herinnering verzenden',
    reminderType: 'Type herinnering',
    firstReminder: '1e herinnering',
    secondReminder: '2e herinnering',
    formalNotice: 'Ingebrekestelling',
    includePdf: 'PDF van de factuur bijvoegen',
    send: 'Verzenden',
    cancel: 'Annuleren',
    sending: 'Verzenden...',
    warning: 'Waarschuwing',
    formalWarning: 'De ingebrekestelling is een juridisch document. Zorg ervoor dat de schuld bevestigd is.',
    notConfigured: 'E-mailservice niet geconfigureerd. Voeg SENDGRID_API_KEY en SENDER_EMAIL toe aan het backend/.env bestand',
  };

  const handleSend = () => {
    if (sendType === 'invoice') {
      onSendInvoice(invoice, includePdf);
    } else {
      onSendReminder(invoice, reminderType, includePdf);
    }
  };

  if (!emailStatus.configured) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {t.warning}
          </h3>
          <p className="text-gray-600 mb-6">{t.notConfigured}</p>
          <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono text-gray-700 mb-6">
            SENDGRID_API_KEY=your_key_here<br />
            SENDER_EMAIL=your@email.com
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            {t.cancel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            {t.title}
          </h3>
        </div>

        <div className="p-6 space-y-4">
          {/* Invoice info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">{t.invoice}</div>
            <div className="font-bold">{invoice.invoice_number}</div>
            <div className="text-sm mt-2 text-gray-500">{t.to}</div>
            <div className="font-medium">{invoice.customer?.email}</div>
          </div>

          {/* Send type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.type}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSendType('invoice')}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  sendType === 'invoice'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t.sendInvoice}
              </button>
              {invoice.status !== 'paid' && (
                <button
                  onClick={() => setSendType('reminder')}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    sendType === 'reminder'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t.sendReminder}
                </button>
              )}
            </div>
          </div>

          {/* Reminder type (if reminder selected) */}
          {sendType === 'reminder' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.reminderType}</label>
              <select
                value={reminderType}
                onChange={(e) => setReminderType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="first">{t.firstReminder}</option>
                <option value="second">{t.secondReminder}</option>
                <option value="formal">{t.formalNotice}</option>
              </select>
              {reminderType === 'formal' && (
                <p className="mt-2 text-sm text-red-600 flex items-start gap-1">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {t.formalWarning}
                </p>
              )}
            </div>
          )}

          {/* Include PDF */}
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input
              type="checkbox"
              checked={includePdf}
              onChange={(e) => setIncludePdf(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <span>{t.includePdf}</span>
          </label>
        </div>

        <div className="p-6 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className={`flex-1 px-4 py-2 rounded-lg text-white flex items-center justify-center gap-2 ${
              sendType === 'reminder' 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            } ${sending ? 'opacity-50 cursor-wait' : ''}`}
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t.sending}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t.send}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSendModal;
